import { t } from 'i18next';

import type { FetchBaseQueryError, FetchBaseQueryMeta } from '@reduxjs/toolkit/query';

interface AuthError {
    message: string;
    code: string | number | null;
    raw: unknown;
}

const isRecord = (value: unknown): value is Record<string, unknown> =>
    typeof value === 'object' && value !== null;

// Match the previous auth transport: retain JSON envelopes and plain token strings.
export async function authResponseHandler(response: Response): Promise<unknown> {
    const text = await response.text();
    try {
        return JSON.parse(text) as unknown;
    } catch {
        return text;
    }
}

/** Keep transport failures serializable in RTK, then restore Error identity for auth callers. */
export function transformAuthError(
    error: FetchBaseQueryError,
    meta?: FetchBaseQueryMeta,
): AuthError {
    const response = meta?.response;
    const status =
        response?.status ?? (typeof error.status === 'number' ? error.status : undefined);
    if (status !== undefined) {
        const data = 'data' in error ? error.data : undefined;
        const message = isRecord(data) ? data.message : undefined;
        const code = isRecord(data) ? data.code : undefined;
        const isHtml = response?.headers.get('content-type')?.includes('text/html');
        return {
            message: isHtml
                ? String(t('msgRsrcNotFnd'))
                : String(message || data || response?.statusText || 'Server error'),
            code: Number(code || status || 0),
            raw: {
                data,
                status,
                statusText: response?.statusText,
                headers: response ? Object.fromEntries(response.headers.entries()) : {},
            },
        };
    }

    return {
        message: 'error' in error ? error.error : 'Unknown error',
        code: null,
        raw: error,
    };
}

export async function unwrapAuthRequest<Result>(request: {
    unwrap: () => Promise<Result>;
    reset: () => void;
}): Promise<Result> {
    try {
        return await request.unwrap();
    } catch (error: unknown) {
        if (error instanceof Error) throw error;
        const message = isRecord(error) ? error.message : undefined;
        throw Object.assign(new Error(typeof message === 'string' ? message : 'Unknown error'), {
            code: isRecord(error) ? (error.code ?? null) : null,
            raw: isRecord(error) ? (error.raw ?? error) : error,
        });
    } finally {
        request.reset();
    }
}
